import type { MenuItem } from '@accesso/cc-std-lib';
import type { OnDestroy } from '@angular/core';
import { ChangeDetectionStrategy, Component, computed, DOCUMENT, ElementRef, inject, model, signal, viewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterLink, RouterOutlet } from '@angular/router';
import { DomHandler } from 'primeng/dom';
import { filter, map } from 'rxjs';

import { EventName, EventService, WINDOW } from '@accesso/cc-std-lib';
import { FooterComponent, MiniCartComponent } from '@accesso/cc-ui';
import { LocaleModel } from '@accesso/cc-ui/model';
import { LocalizePipe } from '@accesso/cc-ui/pipes/localize';
import { JsonApiService } from '@accesso/ng-json-api';
import { BannersComponent, FloatingBannersComponent } from '@accesso/ng-uds/banners';
import { CardComponent } from '@accesso/ng-uds/card';
import { DividerComponent } from '@accesso/ng-uds/divider';
import { MegaMenuComponent } from '@accesso/ng-uds/mega-menu';
import { MenuComponent } from '@accesso/ng-uds/menu';
import { SidebarComponent } from '@accesso/ng-uds/sidebar';
import { UdTemplateDirective } from '@accesso/ng-uds/ud-template';
import {
	VerticalNavigationComponent,
	VerticalNavigationLanguageSelectionComponent,
	VerticalNavigationLinksComponent,
	VerticalNavigationLoginComponent,
} from '@accesso/ng-uds/vertical-navigation';

import { AuthService } from '../../core/auth/auth.service';
import { EnvironmentService } from '../../core/environment/environment.service';
import { STORE_LOCALES } from '../../core/localization/locales';
import { CommonSection } from '../../core/localization/locales/common';
import { CartStore } from '../cart/cart.store';
import { MerchantStore } from '../merchant/merchant.store';

@Component({
	selector: 'app-application-shell',
	imports: [
		BannersComponent,
		FloatingBannersComponent,
		CardComponent,
		SidebarComponent,
		RouterOutlet,
		RouterLink,
		DividerComponent,
		LocalizePipe,
		MegaMenuComponent,
		UdTemplateDirective,
		VerticalNavigationComponent,
		VerticalNavigationLoginComponent,
		VerticalNavigationLinksComponent,
		VerticalNavigationLanguageSelectionComponent,
		FooterComponent,
		MiniCartComponent,
		MenuComponent,
	],
	templateUrl: './application-shell.component.html',
	styleUrl: './application-shell.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ApplicationShellComponent implements OnDestroy {
	// Injected dependencies
	private readonly router = inject(Router);
	private readonly event = inject(EventService);
	private readonly jsonApi = inject(JsonApiService);
	private readonly document = inject(DOCUMENT);
	private readonly window = inject(WINDOW);
	private readonly udVerticalNav = viewChild.required(VerticalNavigationComponent, { read: ElementRef });
	private readonly env = inject(EnvironmentService);
	private readonly auth = inject(AuthService);
	private hideMiniCartCallback: ((event: Event) => void) | null = null;
	private readonly commonLocale = this.jsonApi.findOne(LocaleModel, CommonSection);

	// Public properties
	locales = STORE_LOCALES;
	basket = inject(CartStore).basket;
	merchantStore = inject(MerchantStore);
	verticalNavOpen = model(false);
	enableMiniCart = signal(false);
	enableLogin = signal<boolean>(false);
	year = new Date().getFullYear();
	readonly profile = this.auth.profile;

	profileMenu = computed<MenuItem[]>(() => {
		const { commonLocale } = this;
		if (commonLocale.state().loading) return [];
		return [{ label: commonLocale.attributes()['logout'] ?? 'Logout', command: () => this.logout() }];
	});

	logo = computed(() => {
		const logo = this.merchantStore.merchant()?.settings()?.media?.logo;
		const { assets } = this.env;
		return logo ? `https://${assets}/${logo}` : null;
	});

	hideCheckout = toSignal(
		this.router.events.pipe(
			filter((event) => event instanceof NavigationEnd),
			map((event: NavigationEnd) => event.urlAfterRedirects.startsWith('/cart'))
		)
	);

	// Navigate to cart page and hide mini cart
	viewCart(): void {
		this.hideMiniCart();
		this.router.navigate(['cart']);
	}

	// Show mini cart and set up event listener for closing
	showMiniCart(event: Event): void {
		this.event.publish({ event: EventName.VIEW_MINI_CART });

		const {
			hideMiniCartCallback,
			document: { body },
		} = this;

		if (hideMiniCartCallback) {
			body.removeEventListener('click', hideMiniCartCallback);
			this.hideMiniCartCallback = null;
		}

		this.enableMiniCart.set(true);

		const callback = (): void => this.hideMiniCart();

		this.hideMiniCartCallback = callback;
		body.addEventListener('click', callback);
		event.stopPropagation();
	}

	// Show vertical navigation and focus on the first focusable element
	showVerticalNav() {
		this.verticalNavOpen.set(true);
		setTimeout(() => DomHandler.getFocusableElement(this.udVerticalNav().nativeElement.querySelector('.content'))?.focus(), 250);
	}

	// Handle navigation based on selected menu item
	itemSelected(item: MenuItem): void {
		switch (item.type) {
			case 'external':
				this.window.open(item.value, '_blank');
				break;
			case 'product':
				this.verticalNavOpen.set(false);
				this.router.navigate(['product', item.value]);
				break;
			case 'page-template':
				this.verticalNavOpen.set(false);
				this.router.navigate(['page', item.value]);
				break;
		}
	}

	// Hide mini cart and remove event listener
	hideMiniCart(): void {
		this.enableMiniCart.set(false);

		const { hideMiniCartCallback } = this;
		if (hideMiniCartCallback) {
			this.document.body.removeEventListener('click', hideMiniCartCallback);
			this.hideMiniCartCallback = null;
		}
	}

	ngOnDestroy(): void {
		this.hideMiniCart();
	}

	logout(): void {
		this.event.publish({
			event: EventName.LOGOUT,
			details: {
				id: this.auth.profile()?.id ?? '',
			},
		});
		this.auth.signOut().then((value) => {
			console.warn('signed out', value);
			this.auth.signOutCallback();
		});
	}
}
